import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../contexts/AuthContext';
import { useGuardian } from '../contexts/GuardianContext';

const {
  FiUsers,
  FiPlus,
  FiMail,
  FiX,
  FiSave,
  FiTrash2,
  FiCheckCircle,
  FiAlertCircle,
  FiCopy,
  FiClock,
  FiUserCheck
} = FiIcons;

function GuardianManagement() {
  const { user } = useAuth();
  const {
    guardians,
    inviteGuardian,
    removeGuardian,
    getPendingInvitations,
    cancelInvitation
  } = useGuardian();

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [inviteForm, setInviteForm] = useState({
    name: '',
    email: '',
    relationship: 'parent'
  });
  const [generatedLink, setGeneratedLink] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadPendingInvitations();
  }, []);

  const loadPendingInvitations = async () => {
    try {
      const invitations = await getPendingInvitations();
      setPendingInvitations(invitations);
    } catch (error) {
      console.error('Error loading invitations:', error);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleInviteGuardian = async () => {
    if (!inviteForm.name || !inviteForm.email) {
      showMessage('error', 'Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      const result = await inviteGuardian(inviteForm);
      
      setGeneratedLink(result.inviteLink);
      showMessage('success', 'Invitation created successfully! Share the link below.');
      
      // Reset form
      setInviteForm({
        name: '',
        email: '',
        relationship: 'parent'
      });

      // Reload pending invitations
      await loadPendingInvitations();
    } catch (error) {
      showMessage('error', error.message || 'Failed to create invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    showMessage('success', 'Invitation link copied to clipboard!');
  };

  const handleRemoveGuardian = async (guardianId) => {
    if (!window.confirm('Are you sure you want to remove this guardian? They will lose access to your account.')) {
      return;
    }

    try {
      await removeGuardian(guardianId);
      showMessage('success', 'Guardian removed successfully');
    } catch (error) {
      showMessage('error', 'Failed to remove guardian');
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }

    try {
      await cancelInvitation(invitationId);
      await loadPendingInvitations();
      showMessage('success', 'Invitation cancelled successfully');
    } catch (error) {
      showMessage('error', 'Failed to cancel invitation');
    }
  };

  const getRelationshipLabel = (relationship) => {
    const labels = {
      parent: 'Parent',
      guardian: 'Legal Guardian',
      coach: 'Coach',
      mentor: 'Mentor',
      other: 'Other'
    };
    return labels[relationship] || relationship;
  };

  return (
    <div className="space-y-6">
      {/* Success/Error Message */}
      {message.text && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-lg p-4 flex items-start space-x-3 ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200'
              : 'bg-red-50 border border-red-200'
          }`}
        >
          <SafeIcon
            icon={message.type === 'success' ? FiCheckCircle : FiAlertCircle}
            className={`w-5 h-5 mt-0.5 ${
              message.type === 'success' ? 'text-green-600' : 'text-red-600'
            }`}
          />
          <p
            className={`text-sm ${
              message.type === 'success' ? 'text-green-800' : 'text-red-800'
            }`}
          >
            {message.text}
          </p>
        </motion.div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-space-grotesk font-bold text-secondary">
            Guardian Management
          </h2>
          <p className="text-gray-600 mt-1">
            Invite parents or guardians to co-manage your account
          </p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors btn-primary flex items-center space-x-2"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Invite Guardian</span>
        </button>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
        <div className="flex items-start space-x-3">
          <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-gray-800 mb-1">
              What Guardians Can Do
            </h4>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li>View and edit your complete profile</li>
              <li>Manage your school list and communications</li>
              <li>Track milestones and deadlines</li>
              <li>Upload and manage videos</li>
              <li>Update academic records and achievements</li>
              <li>Full access to all account features</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Active Guardians */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-lg font-space-grotesk font-semibold text-secondary mb-4 flex items-center space-x-2">
          <SafeIcon icon={FiUserCheck} className="w-5 h-5 text-green-600" />
          <span>Active Guardians ({guardians.length})</span>
        </h3>

        {guardians.length === 0 ? (
          <div className="text-center py-8">
            <SafeIcon icon={FiUsers} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No guardians added yet</p>
            <p className="text-sm text-gray-400 mt-1">
              Invite a parent or guardian to help manage your account
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {guardians.map((guardian) => (
              <div
                key={guardian.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiUsers} className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">
                      {guardian.guardian_name}
                    </h4>
                    <div className="flex items-center space-x-3 text-sm text-gray-600">
                      <span>{guardian.guardian_email}</span>
                      <span>•</span>
                      <span>{getRelationshipLabel(guardian.relationship)}</span>
                    </div>
                    {guardian.accepted_at && (
                      <p className="text-xs text-gray-500 mt-1">
                        Active since {new Date(guardian.accepted_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveGuardian(guardian.id)}
                  className="text-red-500 hover:text-red-700 transition-colors p-2"
                  title="Remove guardian"
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-space-grotesk font-semibold text-secondary mb-4 flex items-center space-x-2">
            <SafeIcon icon={FiClock} className="w-5 h-5 text-yellow-600" />
            <span>Pending Invitations ({pendingInvitations.length})</span>
          </h3>

          <div className="space-y-3">
            {pendingInvitations.map((invitation) => (
              <div
                key={invitation.id}
                className="flex items-center justify-between p-4 border border-yellow-200 bg-yellow-50 rounded-lg"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiMail} className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">
                      {invitation.guardian_name}
                    </h4>
                    <div className="flex items-center space-x-3 text-sm text-gray-600">
                      <span>{invitation.guardian_email}</span>
                      <span>•</span>
                      <span>{getRelationshipLabel(invitation.relationship)}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Expires {new Date(invitation.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleCancelInvitation(invitation.id)}
                  className="text-red-500 hover:text-red-700 transition-colors p-2"
                  title="Cancel invitation"
                >
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      <AnimatePresence>
        {showInviteModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50"
              onClick={() => {
                setShowInviteModal(false);
                setGeneratedLink('');
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-space-grotesk font-bold text-secondary">
                      Invite Guardian
                    </h2>
                    <button
                      onClick={() => {
                        setShowInviteModal(false);
                        setGeneratedLink('');
                      }}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <SafeIcon icon={FiX} className="w-6 h-6" />
                    </button>
                  </div>

                  {!generatedLink ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Guardian Name *
                        </label>
                        <input
                          type="text"
                          value={inviteForm.name}
                          onChange={(e) =>
                            setInviteForm({ ...inviteForm, name: e.target.value })
                          }
                          placeholder="e.g., John Smith"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          value={inviteForm.email}
                          onChange={(e) =>
                            setInviteForm({ ...inviteForm, email: e.target.value })
                          }
                          placeholder="guardian@example.com"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Relationship
                        </label>
                        <select
                          value={inviteForm.relationship}
                          onChange={(e) =>
                            setInviteForm({ ...inviteForm, relationship: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="parent">Parent</option>
                          <option value="guardian">Legal Guardian</option>
                          <option value="coach">Coach</option>
                          <option value="mentor">Mentor</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-xs text-blue-800">
                          <strong>Note:</strong> The guardian will receive an invitation link
                          that expires in 7 days. They will need to create an account or sign
                          in to accept the invitation.
                        </p>
                      </div>

                      <div className="flex items-center justify-end space-x-3 pt-4">
                        <button
                          onClick={() => {
                            setShowInviteModal(false);
                            setGeneratedLink('');
                          }}
                          className="px-6 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleInviteGuardian}
                          disabled={loading}
                          className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <SafeIcon icon={FiSave} className="w-4 h-4" />
                          <span>{loading ? 'Creating...' : 'Create Invitation'}</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <SafeIcon
                            icon={FiCheckCircle}
                            className="w-5 h-5 text-green-600 mt-0.5"
                          />
                          <div>
                            <h4 className="font-semibold text-green-800 mb-1">
                              Invitation Created!
                            </h4>
                            <p className="text-sm text-green-700">
                              Share the link below with {inviteForm.name}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Invitation Link
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={generatedLink}
                            readOnly
                            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm"
                          />
                          <button
                            onClick={handleCopyLink}
                            className="bg-gray-100 text-gray-600 p-2 rounded-lg hover:bg-gray-200 transition-colors"
                            title="Copy link"
                          >
                            <SafeIcon icon={FiCopy} className="w-5 h-5" />
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          This link expires in 7 days
                        </p>
                      </div>

                      <button
                        onClick={() => {
                          setShowInviteModal(false);
                          setGeneratedLink('');
                        }}
                        className="w-full bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors btn-primary"
                      >
                        Done
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export default GuardianManagement;